import { CompraAnimalesResponse } from "../modulos/pesaje/ganaderia/response/CompraAnimalesResponse";
import { ClienteFincaLoteResponse } from "../modulos/terceros/response/ClienteFincaLoteResponse";
import { daysBetween, daysFrom } from "./fechaUtils";

/** Proyecta el peso estimado total del lote a hoy sumando la ganancia diaria
 *  desde el último peso estimado.
 *  @returns Peso proyectado, o 0 si no hay peso estimado válido. */
export function proyectarPesoEstimado(lote: ClienteFincaLoteResponse): number {
  const peso = lote.ganaderiaPesoEstimadoTotalKg;
  if (peso == null || peso <= 0) return 0;
  if (
    !lote.ganaderiaFechaUltimoPesoEstimado ||
    lote.ganaderiaGananciaDiariaTotalKg == null ||
    lote.ganaderiaGananciaDiariaTotalKg <= 0
  )
    return peso;
  const dias = daysFrom(lote.ganaderiaFechaUltimoPesoEstimado);
  if (dias <= 0) return peso;
  return peso + lote.ganaderiaGananciaDiariaTotalKg * dias;
}

/** Carga animal del potrero: animales por hectárea.
 *  @returns Animales/ha, o 0 si no hay animales o área. */
export function cargaAnimalPorHa(lote: ClienteFincaLoteResponse): number {
  if (lote.ganaderiaCantidadAnimales <= 0 || lote.areaHectareas <= 0) return 0;
  return lote.ganaderiaCantidadAnimales / lote.areaHectareas;
}

/** Peso promedio por animal según el último pesaje real.
 *  @returns Peso promedio (kg/animal), o null si no aplica (sin animales, sin
 *  peso real > 0, o sin fecha de pesaje). */
export function pesoPromedioReal(
  lote: ClienteFincaLoteResponse,
): number | null {
  if (
    lote.ganaderiaCantidadAnimales <= 0 ||
    lote.ganaderiaPesoRealPromedioKg == null ||
    lote.ganaderiaPesoRealPromedioKg <= 0 ||
    lote.ganaderiaFechaUltimoPesaje == null
  )
    return null;
  return lote.ganaderiaPesoRealPromedioKg;
}

/** Peso promedio por animal según el peso estimado proyectado a hoy.
 *  @returns Peso promedio (kg/animal), o null si no aplica (sin animales o sin
 *  peso estimado > 0). */
export function pesoPromedioEstimado(
  lote: ClienteFincaLoteResponse,
): number | null {
  const animales = lote.ganaderiaCantidadAnimales;
  if (
    animales <= 0 ||
    lote.ganaderiaPesoEstimadoTotalKg == null ||
    lote.ganaderiaPesoEstimadoTotalKg <= 0
  )
    return null;
  return proyectarPesoEstimado(lote) / animales;
}

/** Compras activas (`animalesDisponibles > 0`) cuyo `loteActual` es este lote. */
function _comprasActivasDelLote(
  lote: ClienteFincaLoteResponse,
  compras: CompraAnimalesResponse[],
): CompraAnimalesResponse[] {
  return compras.filter(
    (c) => c.animalesDisponibles > 0 && c.loteActual.id === lote.id,
  );
}

/** Promedio de `valor(compra)` ponderado por `animalesDisponibles`.
 *  @returns `null` si `activas` está vacío. */
function _promedioPonderado(
  activas: CompraAnimalesResponse[],
  valor: (c: CompraAnimalesResponse) => number,
): number | null {
  const totalAnimales = activas.reduce((s, c) => s + c.animalesDisponibles, 0);
  if (totalAnimales <= 0) return null;
  return (
    activas.reduce((s, c) => s + valor(c) * c.animalesDisponibles, 0) /
    totalAnimales
  );
}

/** GDP esperado (kg/día por animal) de un lote: promedio de `gdpEsperadoGr`
 *  (g/día) ponderado por `animalesDisponibles` entre sus compras activas.
 *  @returns `null` si no hay compra activa. */
export function gdpEsperadoKgDia(
  lote: ClienteFincaLoteResponse,
  compras: CompraAnimalesResponse[],
): number | null {
  const promedioGr = _promedioPonderado(
    _comprasActivasDelLote(lote, compras),
    (c) => c.gdpEsperadoGr,
  );
  return promedioGr != null ? promedioGr / 1000 : null;
}

/** Peso objetivo de salida (kg/animal) de un lote: promedio de
 *  `pesoTargetSalida` ponderado por `animalesDisponibles` entre sus compras
 *  activas.
 *  @returns `null` si no hay compra activa. */
export function pesoTargetSalidaPromedio(
  lote: ClienteFincaLoteResponse,
  compras: CompraAnimalesResponse[],
): number | null {
  return _promedioPonderado(
    _comprasActivasDelLote(lote, compras),
    (c) => c.pesoTargetSalida,
  );
}

/** GDP real (kg/día por animal) de un lote: `ganaderiaPesoRealPromedioKg`
 *  menos el peso de entrada ponderado por `animalesDisponibles` entre sus
 *  compras activas, sobre los días ponderados igual (cada compra cuenta sus
 *  propios días desde `fechaCompra`, no una "fecha de entrada promedio"
 *  reconstruida — eso corre la fecha un día en husos horarios negativos).
 *  @returns `null` si no hay pesaje real, no hay compra activa que lo
 *  respalde, o el intervalo de días no es positivo. */
export function gdpRealKgDia(
  lote: ClienteFincaLoteResponse,
  compras: CompraAnimalesResponse[],
): number | null {
  if (
    lote.ganaderiaPesoRealPromedioKg == null ||
    !lote.ganaderiaFechaUltimoPesaje
  ) {
    return null;
  }
  const fechaUltimoPesaje = lote.ganaderiaFechaUltimoPesaje;
  const activas = _comprasActivasDelLote(lote, compras);
  const pesoEntradaPonderado = _promedioPonderado(
    activas,
    (c) => c.pesoPromedioEntrada,
  );
  const diasPonderados = _promedioPonderado(activas, (c) =>
    daysBetween(c.fechaCompra, fechaUltimoPesaje),
  );
  if (
    pesoEntradaPonderado == null ||
    diasPonderados == null ||
    diasPonderados <= 0
  ) {
    return null;
  }
  return (
    (lote.ganaderiaPesoRealPromedioKg - pesoEntradaPonderado) / diasPonderados
  );
}

/** Días de ocupación del potrero (desde el último ingreso).
 *  @returns Días de ocupación, o null si está desocupado o no hay fecha. */
export function diasOcupacion(lote: ClienteFincaLoteResponse): number | null {
  if (lote.ganaderiaCantidadAnimales <= 0 || !lote.ganaderiaUltimoIngreso)
    return null;
  return daysFrom(lote.ganaderiaUltimoIngreso);
}

/** Días de descanso del potrero (desde la última salida).
 *  @returns Días de descanso, o null si está ocupado o no hay fecha. */
export function diasDescanso(lote: ClienteFincaLoteResponse): number | null {
  if (lote.ganaderiaCantidadAnimales > 0 || !lote.ganaderiaUltimaSalida)
    return null;
  return daysFrom(lote.ganaderiaUltimaSalida);
}

/** Días que el lote activo lleva en finca (desde el último ingreso),
 *  independiente de si el potrero está ocupado.
 *  @returns Días en finca, o null si no hay fecha de ingreso. */
export function diasEnFinca(lote: ClienteFincaLoteResponse): number | null {
  if (!lote.ganaderiaUltimoIngreso) return null;
  return daysFrom(lote.ganaderiaUltimoIngreso);
}
